import React from 'react';
import { Circle, Line, Rect, SchematicShell } from './SchematicPrimitives';
import {
  STANDARD_GRAB_BAR,
  systemAccessoryOf,
} from '@shared-lib/shower-systems';
import {
  IN_TO_M,
  SHOWER_ACCESSORY,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * A shower's accessories on an elevation (shower systems step 3a), the
 * outlines their renderers (`showerAccessoryRenderers.js`) fill: a corner
 * shelf, foot shelf or seat as its box seen from the front or the side, a
 * caddy with its shelves, and the grab bar as its tube between two round
 * flanges — from the side, its flange on the wall and the tube standing off.
 */
export function ShowerAccessorySchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  obj = {},
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const stroke = { strokeColor, strokeWidth };
  const side = view === 'left' || view === 'right';
  const part = systemAccessoryOf(obj)?.accessory;
  // a system's grab bar part (Acrylic's corner shelf grab bar) is drawn as
  // its box, as a shelf is; the standard grab bar as its tube
  if (obj.type === 'grab-bar' && !part) {
    return <GrabBar {...{ width, height, depth, side, stroke }} />;
  }
  const across = side ? depth : width;
  // a caddy's boards — and an acrylic tower shelf's, drawn as a caddy
  const shelves =
    obj.type === 'corner-caddy' || part?.shape === 'tower-shelf'
      ? (part?.shelves ?? 2)
      : 0;
  const board = Math.min(SHOWER_ACCESSORY.caddyBoardIn * IN_TO_M, height / 2);
  const gap = (height - board) / (shelves + 1);
  return (
    <SchematicShell width={across} height={height} {...stroke}>
      {Array.from({ length: shelves }, (_, i) => {
        const y = height - board / 2 - gap * (i + 1);
        return <Line key={i} x1={0} y1={y} x2={across} y2={y} {...stroke} />;
      })}
    </SchematicShell>
  );
}

function GrabBar({ width, height, depth, side, stroke }) {
  const bar = STANDARD_GRAB_BAR;
  const r = (bar.diameterIn * IN_TO_M) / 2;
  const flangeR = Math.min((bar.flangeIn * IN_TO_M) / 2, height / 2);
  const flangeT = SHOWER_ACCESSORY.grabBarFlangeIn * IN_TO_M;
  const mid = height / 2;
  if (side) {
    // the flange on the wall (x = 0), the tube's end out from it
    return (
      <SchematicShell
        width={depth}
        height={height}
        drawFrame={false}
        {...stroke}
      >
        <Rect
          x={0}
          y={mid - flangeR}
          width={flangeT}
          height={flangeR * 2}
          {...stroke}
        />
        <Circle cx={depth - r} cy={mid} r={r} {...stroke} />
      </SchematicShell>
    );
  }
  const ends = width / 2 - flangeR;
  return (
    <SchematicShell width={width} height={height} drawFrame={false} {...stroke}>
      <Rect
        x={width / 2 - ends}
        y={mid - r}
        width={ends * 2}
        height={r * 2}
        {...stroke}
      />
      <Circle cx={width / 2 - ends} cy={mid} r={flangeR} {...stroke} />
      <Circle cx={width / 2 + ends} cy={mid} r={flangeR} {...stroke} />
    </SchematicShell>
  );
}
